import { useNavigate } from '@tanstack/react-router';

import { isNewTabClick, useTabs } from '@/features/tabs';

import { type RoutineChatView } from '../routine-chat-view';
import { routinesStore } from '../routines.state';

import type { MouseEvent } from 'react';

/**
 * Navigation into a routine, shared by the sidebar section and the routines list.
 *
 * Opening a routine lands on its newest run. A new tab follows the newest run
 * already, so only the current tab needs its pinned run released.
 */
export function useOpenRoutine() {
  const navigate = useNavigate();
  const { openTab } = useTabs();

  const openRoutineInNewTab = (routineId: number) => {
    openTab({ path: `/routines/${routineId}` });
  };

  const openRoutine = (
    routineId: number,
    event?: MouseEvent,
    view?: RoutineChatView
  ) => {
    if (event && isNewTabClick(event)) {
      openRoutineInNewTab(routineId);
      return;
    }

    routinesStore.unpinReport(routineId);
    navigate({
      to: '/routines/$routineId',
      params: { routineId: String(routineId) },
      search: view ? { view } : undefined,
    });
  };

  /** Opening a given run pins it, so the routine stays on it */
  const openRoutineRun = (routineId: number, reportId: number) => {
    navigate({
      to: '/routines/$routineId',
      params: { routineId: String(routineId) },
      search: { run: reportId },
    });
  };

  const openRoutineConfiguration = (routineId: number) => {
    routinesStore.unpinReport(routineId);
    routinesStore.setShowHistory(true);
    navigate({
      to: '/routines/$routineId',
      params: { routineId: String(routineId) },
      search: { view: 'configure' },
    });
  };

  return {
    openRoutine,
    openRoutineRun,
    openRoutineConfiguration,
    openRoutineInNewTab,
  };
}
